import { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { IUserDto, useUser } from "@framework/provider-user";
import { normalizeUserMultiAnalysisSettings } from "@framework/types";

import { useMultiAnalysis } from "../../../../../../context/multi-analysis-context";
import { TemplateLayoutSelector } from "../template-layout-selector";
import {
  cloneAnalysisTemplate,
  createDraftAnalysisTemplate,
  canDeleteAnalysisTemplate,
  canEditAnalysisTemplate,
  filterAnalysisTemplatesForUser,
  MULTI_ANALYSIS_TEMPLATE_TYPE,
  resizeAnalysisTemplateSettings,
  isStandardAnalysisTemplate,
  type INormalizedAnalysisTemplate,
} from "../../utils/analysis-template-settings";

interface IMultiAnalysisTemplatesPanelProps {
  templates: readonly INormalizedAnalysisTemplate[];
  isLoading: boolean;
  onReloadTemplates: () => Promise<void>;
}

export const MultiAnalysisTemplatesPanel: FC<IMultiAnalysisTemplatesPanelProps> = ({
  templates,
  isLoading,
  onReloadTemplates,
}) => {
  const user = useUser<IUserDto>();
  const {
    selectedTemplateListId,
    setSelectedTemplateListId,
    appliedTemplateId,
    isTemplateEditMode,
    draftTemplate,
    setDraftTemplate,
    startCreateTemplate,
    startEditTemplate,
    cancelTemplateEdit,
    saveTemplateEdit,
    deleteTemplate,
    applySelectedTemplate,
  } = useMultiAnalysis();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const multiAnalysisSettings = useMemo(
    () => normalizeUserMultiAnalysisSettings(user.profile.interfaceSettings.multiAnalysis),
    [user.profile.interfaceSettings.multiAnalysis],
  );

  const visibleTemplates = useMemo(
    () =>
      filterAnalysisTemplatesForUser(templates, {
        userId: user.profile.id,
        showStandardTemplates: multiAnalysisSettings.showStandardTemplates,
        showPublicTemplates: multiAnalysisSettings.showPublicTemplates,
      }),
    [
      multiAnalysisSettings.showPublicTemplates,
      multiAnalysisSettings.showStandardTemplates,
      templates,
      user.profile.id,
    ],
  );

  const selectedListTemplate = useMemo(
    () => visibleTemplates.find(template => template.idAnalysisTemplate === selectedTemplateListId) ?? null,
    [selectedTemplateListId, visibleTemplates],
  );
  const visibleAppliedTemplate = useMemo(
    () => visibleTemplates.find(template => template.idAnalysisTemplate === appliedTemplateId) ?? null,
    [appliedTemplateId, visibleTemplates],
  );
  const isAdmin = user.isAdmin();
  const canEditSelectedTemplate = selectedListTemplate
    ? canEditAnalysisTemplate(selectedListTemplate, user.profile.id, isAdmin)
    : false;
  const canDeleteSelectedTemplate = selectedListTemplate
    ? canDeleteAnalysisTemplate(selectedListTemplate, user.profile.id, isAdmin)
    : false;

  useEffect(() => {
    if (isLoading || isSaving) {
      return;
    }

    if (!visibleTemplates.length) {
      if (selectedTemplateListId !== null) {
        setSelectedTemplateListId(null);
      }
      return;
    }

    const defaultTemplateId =
      visibleAppliedTemplate?.idAnalysisTemplate ??
      visibleTemplates.find(template => template.type === MULTI_ANALYSIS_TEMPLATE_TYPE.STANDARD)?.idAnalysisTemplate ??
      visibleTemplates[0]?.idAnalysisTemplate ??
      null;

    if (
      selectedTemplateListId === null ||
      !visibleTemplates.some(template => template.idAnalysisTemplate === selectedTemplateListId)
    ) {
      if (defaultTemplateId !== selectedTemplateListId) {
        setSelectedTemplateListId(defaultTemplateId);
      }
    }
  }, [
    isLoading,
    isSaving,
    selectedTemplateListId,
    setSelectedTemplateListId,
    visibleAppliedTemplate?.idAnalysisTemplate,
    visibleTemplates,
  ]);

  const editingTemplate = isTemplateEditMode ? draftTemplate : null;

  const handleLayoutChange = (layout: INormalizedAnalysisTemplate["settings"]["layout"]) => {
    if (!draftTemplate) {
      return;
    }

    setDraftTemplate({
      ...draftTemplate,
      settings: resizeAnalysisTemplateSettings(draftTemplate.settings, layout),
    });
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const saved = await saveTemplateEdit();
      if (saved) {
        await onReloadTemplates();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    const deleted = await deleteTemplate();
    setDeleteDialogOpen(false);
    if (deleted) {
      await onReloadTemplates();
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box sx={{ p: 1.25 }}>
        <Stack spacing={1}>
          <TextField
            select
            size="small"
            fullWidth
            label={<FormattedMessage id="pages.multiAnalysis.templates.list" />}
            value={selectedTemplateListId ?? ""}
            disabled={isLoading || isSaving || !visibleTemplates.length || isTemplateEditMode}
            onChange={event => setSelectedTemplateListId(Number(event.target.value))}
          >
            {visibleTemplates.map(template => (
              <MenuItem key={template.idAnalysisTemplate} value={template.idAnalysisTemplate}>
                {template.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            size="small"
            disabled={!selectedTemplateListId || isTemplateEditMode || isSaving}
            onClick={() => applySelectedTemplate()}
          >
            <FormattedMessage id="pages.multiAnalysis.templateActions.select" />
          </Button>
          <Stack direction="row" spacing={0.75}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              disabled={isTemplateEditMode || isSaving}
              onClick={() => startCreateTemplate(createDraftAnalysisTemplate(user.profile.id))}
            >
              <FormattedMessage id="pages.multiAnalysis.templateActions.create" />
            </Button>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              disabled={!canEditSelectedTemplate || isTemplateEditMode || isSaving}
              onClick={() => {
                if (selectedListTemplate) {
                  startEditTemplate(cloneAnalysisTemplate(selectedListTemplate));
                }
              }}
            >
              <FormattedMessage id="pages.multiAnalysis.templateActions.edit" />
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Divider />

      <Box
        sx={{
          p: 1.25,
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          opacity: isTemplateEditMode ? 1 : 0.55,
          pointerEvents: isTemplateEditMode ? "auto" : "none",
        }}
      >
        {editingTemplate ? (
          <Stack spacing={1.25}>
            <TextField
              size="small"
              label={<FormattedMessage id="pages.multiAnalysis.templateEditor.name" />}
              value={editingTemplate.name}
              onChange={event =>
                setDraftTemplate({
                  ...editingTemplate,
                  name: event.target.value,
                })
              }
            />
            <TemplateLayoutSelector layout={editingTemplate.settings.layout} onChange={handleLayoutChange} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingTemplate.isPublic}
                  onChange={event => {
                    const isPublic = event.target.checked;

                    setDraftTemplate({
                      ...editingTemplate,
                      isPublic,
                      idUser:
                        isStandardAnalysisTemplate(editingTemplate) && isPublic
                          ? null
                          : editingTemplate.idUser ?? user.profile.id,
                    });
                  }}
                />
              }
              label={<FormattedMessage id="pages.multiAnalysis.templateEditor.shared" />}
            />
            {isAdmin ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isStandardAnalysisTemplate(editingTemplate)}
                    onChange={event => {
                      const isStandard = event.target.checked;

                      setDraftTemplate({
                        ...editingTemplate,
                        type: isStandard ? MULTI_ANALYSIS_TEMPLATE_TYPE.STANDARD : MULTI_ANALYSIS_TEMPLATE_TYPE.CUSTOM,
                        idUser: isStandard && editingTemplate.isPublic ? null : user.profile.id,
                      });
                    }}
                  />
                }
                label={<FormattedMessage id="pages.multiAnalysis.templateEditor.standard" />}
              />
            ) : null}
            <Typography sx={{ fontSize: 11, color: "#8b93a7" }}>
              <FormattedMessage id="pages.multiAnalysis.templateEditor.editHint" />
            </Typography>
          </Stack>
        ) : (
          <Typography sx={{ fontSize: 12, color: "#8b93a7" }}>
            <FormattedMessage id="pages.multiAnalysis.templateEditor.inactive" />
          </Typography>
        )}
      </Box>

      <Divider />

      <Box sx={{ p: 1.25, display: "grid", gap: 0.75, gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
        <Button
          variant="contained"
          size="small"
          disabled={!isTemplateEditMode || isSaving}
          onClick={() => void handleSave()}
        >
          <FormattedMessage id="pages.multiAnalysis.templateActions.save" />
        </Button>
        <Button variant="outlined" size="small" disabled={!isTemplateEditMode || isSaving} onClick={cancelTemplateEdit}>
          <FormattedMessage id="pages.multiAnalysis.templateActions.cancel" />
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={!canDeleteSelectedTemplate || isTemplateEditMode || isSaving}
          onClick={() => setDeleteDialogOpen(true)}
        >
          <FormattedMessage id="pages.multiAnalysis.templateActions.delete" />
        </Button>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <FormattedMessage id="pages.multiAnalysis.templateActions.deleteConfirmTitle" />
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            <FormattedMessage
              id="pages.multiAnalysis.templateActions.deleteConfirmText"
              values={{ name: selectedListTemplate?.name ?? "" }}
            />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            <FormattedMessage id="pages.multiAnalysis.templateActions.cancel" />
          </Button>
          <Button color="error" variant="contained" onClick={() => void handleDelete()}>
            <FormattedMessage id="pages.multiAnalysis.templateActions.delete" />
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
